'use client';

/**
 * Voice Trainer Modal
 * Guides users through recording voice samples to create a custom ElevenLabs voice
 * 
 * States:
 * 1. Not logged in → Sign-in prompt
 * 2. Non-subscriber → Upsell to /pricing
 * 3. Subscriber, no training → Recording interface
 * 4. Training in progress → Progress display
 * 5. Training complete → Success message
 */

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from 'next-auth/react';
import { X, Mic, Square, Upload, CheckCircle, Loader2, AlertCircle, Smartphone, Monitor, Crown, Sparkles, Zap, Star, Play } from 'lucide-react';
import QRCode from 'qrcode';

interface VoiceTrainingStatus {
  id: string;
  elevenLabsVoiceId: string | null;
  status: 'pending' | 'collecting' | 'training' | 'ready' | 'failed';
  assignedBookId: string | null;
  samplesUploaded: number;
  requiredSamples: number;
  createdAt: string;
  updatedAt: string;
}

interface VoiceTrainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export default function VoiceTrainerModal({ isOpen, onClose, onComplete }: VoiceTrainerModalProps) {
  const { data: session, status: sessionStatus } = useSession();
  const [voiceStatus, setVoiceStatus] = useState<VoiceTrainingStatus | null>(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(false); // Start as false, only set true when actually loading
  const [error, setError] = useState<string | null>(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const [consentGiven] = useState(true); // Auto-consent: implicit by using the feature
  const [micPermissionState, setMicPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'checking'>('checking');
  
  // Audio visualization state
  const [audioLevel, setAudioLevel] = useState(0);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const textContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Training text for voice samples (optimized for voice cloning)
  // Each text is 60-75 seconds, designed for different emotional tones and phonetic variety
  const VOICE_TRAINING_TEXTS = [
    // Sample 1: Neutral/Clear Narration - Steady pace, clear pronunciation
    "Welcome to a world where stories come alive and imagination knows no boundaries. Every book holds countless adventures waiting to be discovered. When you open these pages, you'll find magical kingdoms hidden beneath the waves, brave heroes climbing mountains that touch the clouds, and gentle creatures who teach us about kindness and friendship. Reading together creates special moments that children treasure forever. The sound of a familiar voice sharing tales of wonder becomes a cherished memory that lasts a lifetime.",
    
    // Sample 2: Warm/Gentle - Soft, comforting bedtime story tone
    "Once upon a time, in a cozy little cottage surrounded by whispering willow trees, there lived a family of rabbits who loved bedtime stories more than anything in the whole wide world. Every evening, as the golden sun set behind the purple hills, the youngest bunny would snuggle close and say, 'Please, tell me about the stars.' And so the story would begin, soft and sweet like honey, about constellations that danced across the velvet sky, each one holding ancient secrets and beautiful dreams for all who gazed upward with wonder.",
    
    // Sample 3: Excited/Energetic - Dynamic, adventurous storytelling  
    "Suddenly, the dragon burst through the clouds with a thunderous roar that shook the entire mountain! The brave knight gasped in amazement—this wasn't a scary dragon at all! Its scales shimmered like rainbows, and its eyes sparkled with mischief and joy. 'Quick!' shouted the dragon with excitement. 'Hop on my back! We're going on the most incredible adventure you've ever imagined!' Together they soared higher and higher, swooping through canyons, racing with eagles, and discovering secret waterfalls that sang magical melodies. Every moment brought something new and absolutely amazing!",
    
    // Sample 4: Mysterious/Dramatic - Varied pacing, suspenseful
    "Deep in the heart of the ancient forest, where shadows danced between towering oaks and mysterious fog curled around twisted roots, something extraordinary was about to happen. The old map had led them here... but why? What secrets lay hidden beneath these weathered stones? The young explorer's heart raced with anticipation. Every rustle of leaves, every distant owl's hoot, every crack of a twig seemed to whisper clues to an age-old puzzle. Then, just as the moonlight broke through the canopy, illuminating a curious symbol carved into the bark... they understood. This wasn't just any forest. This was the beginning of a legend.",
    
    // Sample 5: Joyful/Uplifting - Expressive, celebratory
    "And that's when everyone realized the most wonderful truth: they didn't need magic spells or enchanted objects to make their dreams come true! The real magic had been inside them all along—their courage, their kindness, their creativity, and their love for each other. The whole village erupted in celebration! Music filled the air as children danced and laughed, flowers bloomed in spectacular colors, and even the grumpy old bear couldn't help but smile. It was a day of pure joy and triumph, proving that when we believe in ourselves and work together, absolutely anything is possible! Hooray for happy endings!"
  ];
  
  const currentSampleIndex = voiceStatus?.samplesUploaded || 0;
  const currentTrainingText = VOICE_TRAINING_TEXTS[currentSampleIndex] || VOICE_TRAINING_TEXTS[0];
  
  // Split text into sentences for highlighting
  const currentTrainingSentences = currentTrainingText
    .split(/([.!?]+\s+)/)
    .filter(s => s.trim().length > 0)
    .reduce((acc: string[], curr, i, arr) => {
      if (i % 2 === 0) {
        const sentence = curr + (arr[i + 1] || '');
        acc.push(sentence.trim());
      }
      return acc;
    }, []);

  // QR/Mobile mode state
  const [showQRMode, setShowQRMode] = useState(false);
  const [qrToken, setQrToken] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Timer for recording
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Fetch voice training status
  const fetchStatus = async () => {
    if (sessionStatus !== 'authenticated') {
      console.log('[Voice Training] User not authenticated, skipping API call');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch('/api/voice-training/me');
      
      if (!res.ok) {
        const data = await res.json();
        console.error('[Voice Training] API error:', res.status, data);
        setError(data.error || `Failed to load training status (${res.status})`);
        setLoading(false);
        return;
      }

      const data = await res.json();
      console.log('[Voice Training] Fetched status:', {
        isSubscriber: data.isSubscriber,
        subscriptionPlan: data.subscriptionPlan,
        voiceStatus: data.userVoice?.status,
        elevenLabsVoiceId: data.userVoice?.elevenLabsVoiceId,
        fullUserVoice: data.userVoice
      });

      setVoiceStatus(data.userVoice);
      
      // Check if user has Pro subscription (ONLY Pro allows custom voice training)
      const isProSubscriber = data.subscriptionPlan === 'AMAYA_PRO';
      
      // Also check if user has any valid subscription (Plus or Pro)
      const hasAnySubscription = 
        data.subscriptionPlan === 'AMAYA_PLUS' || 
        data.subscriptionPlan === 'AMAYA_PRO' ||
        (data.isSubscriber === true && data.subscriptionPlan !== 'NONE');
      
      console.log('[Voice Training] Subscription check:', {
        isPro: isProSubscriber,
        hasAnySubscription,
        subscriptionPlan: data.subscriptionPlan,
        isSubscriber: data.isSubscriber
      });
      
      setIsPro(isProSubscriber);
      setIsSubscriber(hasAnySubscription);
    } catch (err) {
      console.error('[Voice Training] Error fetching voice status:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Only fetch status if potentially authenticated
      if (sessionStatus === 'authenticated') {
        fetchStatus();
      } else if (sessionStatus === 'unauthenticated') {
        // User is confirmed not authenticated, don't try to fetch
        console.log('[Voice Training] User not authenticated');
        setLoading(false);
      }
      // Don't do anything if status is 'loading' - wait for it to resolve
    }
  }, [isOpen, sessionStatus]);

  // Only check microphone permissions once we know user is Pro subscriber
  useEffect(() => {
    if (isOpen && isPro && !loading) {
      console.log('[Voice Training] Checking microphone for Pro user');
      checkMicrophonePermission();
    }
  }, [isOpen, isPro, loading]);

  // Check microphone permission
  const checkMicrophonePermission = async () => {
    try {
      // Check if running on HTTPS or localhost
      const isSecure = window.location.protocol === 'https:' || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
      
      if (!isSecure) {
        setError('⚠️ Microphone access requires HTTPS. Please access this page using https://');
        setMicPermissionState('denied');
        return;
      }

      // Check if running in browser (not SSR)
      if (typeof window === 'undefined') {
        return;
      }

      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Your browser doesn\'t support audio recording. Please use Chrome, Firefox, or Safari.');
        setMicPermissionState('denied');
        return;
      }

      // Check current permission state (if supported)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          
          console.log('[Voice Trainer] Microphone permission:', permissionStatus.state);
          setMicPermissionState(permissionStatus.state as any);
          
          if (permissionStatus.state === 'granted') {
            setError(null); // Clear any errors
          }
          // Don't set error for 'denied' state initially - let user try to record first
          // The actual permission prompt will appear when startRecording() calls getUserMedia()
          
          // Listen for permission changes
          permissionStatus.onchange = () => {
            setMicPermissionState(permissionStatus.state as any);
            if (permissionStatus.state === 'granted') {
              setError(null);
            }
            // Don't auto-set error on denied - let the startRecording() function handle it
          };
        } catch (permErr) {
          // Safari might not support microphone permission query
          console.log('[Voice Trainer] Permission query not supported, will prompt on record');
          setMicPermissionState('prompt');
        }
      } else {
        // Fallback: Browser doesn't support permission query API
        setMicPermissionState('prompt');
      }
    } catch (err: any) {
      console.error('[Voice Trainer] Permission check error:', err);
      setMicPermissionState('prompt'); // Assume we can try
    }
  };

  // Audio visualization - analyze volume in real-time
  const startAudioVisualization = (stream: MediaStream) => {
    try {
      // Create audio context and analyzer
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      // Animate volume meter and word highlighting
      const updateVisualization = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average volume (0-100)
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = Math.min(100, (average / 128) * 100);
        setAudioLevel(normalizedLevel);
        
        animationFrameRef.current = requestAnimationFrame(updateVisualization);
      };
      
      updateVisualization();
      
      // Sentence progression timer - proportional to sentence length
      // Calculate word count for each sentence to determine timing
      const sentenceWordCounts = currentTrainingSentences.map(s => s.split(/\s+/).length);
      const totalWords = sentenceWordCounts.reduce((sum, count) => sum + count, 0);
      const totalSeconds = 70; // 70 seconds average
      
      // Calculate time for each sentence based on its proportion of total words
      const sentenceTimings = sentenceWordCounts.map(wordCount => 
        (wordCount / totalWords) * totalSeconds * 1000 // Convert to milliseconds
      );
      
      let currentSentence = 0;
      
      const advanceToNextSentence = () => {
        currentSentence++;
        if (currentSentence < currentTrainingSentences.length) {
          setCurrentSentenceIndex(currentSentence);
          // Schedule next sentence based on its timing
          const nextTimeout = setTimeout(advanceToNextSentence, sentenceTimings[currentSentence]);
          (audioContextRef.current as any)._currentTimeout = nextTimeout;
        }
      };
      
      // Start first sentence progression
      if (sentenceTimings.length > 0) {
        const firstTimeout = setTimeout(advanceToNextSentence, sentenceTimings[0]);
        (audioContextRef.current as any)._currentTimeout = firstTimeout;
      }
      
    } catch (err) {
      console.log('[Voice Trainer] Audio visualization setup failed:', err);
    }
  };
  
  // Stop audio visualization
  const stopAudioVisualization = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (audioContextRef.current) {
      const currentTimeout = (audioContextRef.current as any)._currentTimeout;
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }
    
    analyserRef.current = null;
    setAudioLevel(0);
    setCurrentSentenceIndex(0);
  };
  
  // Start countdown and then recording
  const startRecording = async () => {
    try {
      // Check if running in browser (not SSR)
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        console.log('[Voice Trainer] Not in browser environment');
        return;
      }

      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.log('[Voice Trainer] Browser does not support audio recording, switching to mobile');
        await switchToQRMode();
        return;
      }

      setError(null); // Clear any previous errors
      setCurrentSentenceIndex(0); // Reset sentence highlighting
      
      // Request microphone access
      console.log('[Voice Trainer] Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('[Voice Trainer] Microphone access granted');
      setMicPermissionState('granted');
      setError(null); // Clear any errors
      
      // Start countdown: 3... 2... 1... GO!
      setIsCountingDown(true);
      setCountdown(3);
      
      await new Promise((resolve) => {
        let count = 3;
        const countdownInterval = setInterval(() => {
          count--;
          if (count > 0) {
            setCountdown(count);
          } else {
            setCountdown(null);
            setIsCountingDown(false);
            clearInterval(countdownInterval);
            resolve(true);
          }
        }, 1000); // 1 second per count
      });
      
      // Now start actual recording
      startAudioVisualization(stream);
      
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
        stopAudioVisualization();
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      
      // Auto-scroll to top of text when recording starts
      setTimeout(() => {
        if (textContainerRef.current) {
          textContainerRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    } catch (err: any) {
      console.error('[Voice Trainer] Error starting recording:', err);
      
      // If microphone access denied or any error, automatically switch to mobile QR mode
      console.log('[Voice Trainer] Microphone access failed, switching to mobile recording');
      setMicPermissionState('denied');
      
      // Automatically switch to QR mode for mobile recording
      setTimeout(() => {
        switchToQRMode();
      }, 500); // Small delay to show transition
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // Upload recording
  const uploadRecording = async () => {
    if (!audioBlob) return;

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-sample.webm');

      const res = await fetch('/api/voice-training/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        setVoiceStatus(data.userVoice);
        setAudioBlob(null);
        
        // If training complete, call onComplete callback
        if (data.userVoice.status === 'ready' && onComplete) {
          onComplete();
        }
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload sample. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Format time display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate mobile session token and QR code
  const switchToQRMode = async () => {
    try {
      setQrLoading(true);
      setError(null);
      console.log('[Voice Trainer] Generating mobile session token...');

      const res = await fetch('/api/voice-training/mobile-session', {
        method: 'POST',
      });

      const data = await res.json();

      if (res.ok) {
        const token = data.token;
        setQrToken(token);

        // Generate QR code
        const baseUrl = window.location.origin;
        const mobileUrl = `${baseUrl}/voice-training/mobile/${token}`;
        const qrDataUrl = await QRCode.toDataURL(mobileUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#7C3AED', // purple-600
            light: '#FFFFFF',
          },
        });

        setQrDataUrl(qrDataUrl);
        setShowQRMode(true);

        // Start polling for updates
        startPolling();

        console.log('[Voice Trainer] QR mode activated, token:', token);
      } else {
        setError(data.error || 'Failed to generate QR code');
      }
    } catch (err) {
      console.error('[Voice Trainer] QR generation error:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setQrLoading(false);
    }
  };

  // Start polling for voice status updates (when in QR mode)
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log('[Voice Trainer] Starting status polling...');
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch('/api/voice-training/me');
        const data = await res.json();

        if (res.ok && data.userVoice) {
          const prevSamples = voiceStatus?.samplesUploaded || 0;
          const newSamples = data.userVoice.samplesUploaded || 0;

          setVoiceStatus(data.userVoice);

          // Log progress
          if (newSamples > prevSamples) {
            console.log(`[Voice Trainer] Progress update: ${newSamples}/${data.userVoice.requiredSamples} samples`);
          }

          // Stop polling if complete
          if (data.userVoice.status === 'ready') {
            console.log('[Voice Trainer] Training complete, stopping poll');
            stopPolling();
            if (onComplete) {
              onComplete();
            }
          }
        }
      } catch (err) {
        console.error('[Voice Trainer] Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('[Voice Trainer] Polling stopped');
    }
  };

  // Switch back to desktop recording mode
  const switchToDesktopMode = () => {
    setShowQRMode(false);
    setQrToken(null);
    setQrDataUrl(null);
    stopPolling();
  };

  // Cleanup on unmount or modal close
  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setShowQRMode(false);
      setQrToken(null);
      setQrDataUrl(null);
    }
  }, [isOpen]);

  // Auto-switch to QR mode disabled - users can manually click "Use Phone Instead"
  // This was too aggressive and prevented the microphone prompt from appearing
  // useEffect(() => {
  //   if (error && error.includes('Microphone')) {
  //     const timer = setTimeout(() => {
  //       if (sessionStatus === 'authenticated' && isSubscriber) {
  //         console.log('[Voice Trainer] Auto-switching to QR mode due to microphone error');
  //         switchToQRMode();
  //       }
  //     }, 2000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [error, sessionStatus, isSubscriber]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Animated Background Overlay */}
      <style jsx global>{`
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .voice-modal-overlay {
          animation: modalFadeIn 0.2s ease-out;
        }
        .voice-modal-content {
          animation: modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
      
      <div 
        className="voice-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        onClick={onClose}
      >
        <div 
          className="voice-modal-content relative w-full max-w-3xl max-h-[85vh] overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
            borderRadius: '24px',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative Header Gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '140px',
            background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 50%, #F8C8A0 100%)',
            opacity: 0.15,
            pointerEvents: 'none',
            borderRadius: '24px 24px 0 0',
          }} />

          {/* Header */}
          <div style={{
            position: 'relative',
            padding: '1.5rem',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #B4A0E5 0%, #C9B6E4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(180, 160, 229, 0.3)',
              }}>
                <Mic className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  margin: 0,
                  background: 'linear-gradient(135deg, #7B68EE 0%, #B4A0E5 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Create Your Voice
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  color: '#64748B',
                  fontWeight: 500,
                }}>
                  Professional AI Voice in Minutes
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                background: 'rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X className="w-5 h-5" style={{ color: '#64748B' }} />
            </button>
          </div>

        {/* Content */}
        <div className="px-6 py-6" style={{ maxHeight: 'calc(85vh - 100px)', overflowY: 'auto' }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading your training status...</p>
            </div>
          ) : error && sessionStatus === 'authenticated' ? (
            /* API Error State */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Connection Error
              </h3>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    setError(null);
                    fetchStatus();
                  }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          ) : sessionStatus !== 'authenticated' ? (
            /* STATE 1: Not logged in */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sign In to Continue
              </h3>
              <p className="text-gray-600 mb-6">
                Create a custom voice by recording short audio samples.
                Sign in to get started, then upgrade to Amaya Pro.
              </p>
              <button
                onClick={() => (window.location.href = '/login')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
              >
                Sign In
              </button>
            </div>
          ) : !isPro ? (
            /* STATE 2: Not Pro subscriber (includes Free and Plus users) */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Amaya Pro Required
              </h3>
              <p className="text-gray-600 mb-6">
                Custom voice training is exclusively available for <strong>Amaya Pro</strong> subscribers.
                Upgrade to create audiobooks with your own voice!
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Record 5 voice samples (60-75 seconds each for best quality)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Create a personalized AI voice in minutes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Generate audiobooks that sound like you</span>
                  </li>
                </ul>
              </div>
              <button
                onClick={() => (window.location.href = '/pricing')}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-medium"
              >
                View Pricing
              </button>
            </div>
          ) : voiceStatus?.status === 'ready' ? (
            /* STATE 5: Training complete */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Your Voice is Ready! 🎉
              </h3>
              <p className="text-gray-600 mb-4">
                You've successfully completed voice training with {voiceStatus.samplesUploaded} samples.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  You can now use your custom voice when creating audiobooks.
                  Look for the "Use my voice" toggle in the creation form.
                </p>
              </div>
              <button
                onClick={() => {
                  if (onComplete) {
                    onComplete();
                  }
                  onClose();
                }}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
              >
                Start Creating
              </button>
            </div>
          ) : voiceStatus?.status === 'training' ? (
            /* STATE 4: Training in progress */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Training Your Voice...
              </h3>
              <p className="text-gray-600 mb-6">
                Please wait while we process your voice samples.
                This usually takes less than 30 seconds.
              </p>
              <div className="max-w-xs mx-auto">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                </div>
              </div>
            </div>
          ) : showQRMode ? (
            /* QR Mode: Mobile Recording */
            <div className="text-center py-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                🎤 Record on Your Phone
              </h3>
              <p className="text-gray-600 mb-6">
                Scan this QR code with your phone to start recording
              </p>

              {/* QR Code Display */}
              {qrLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
                </div>
              ) : qrDataUrl ? (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 mb-6">
                  <div className="bg-white rounded-xl p-4 inline-block shadow-lg">
                    <img src={qrDataUrl} alt="QR Code" className="w-64 h-64" />
                  </div>
                </div>
              ) : null}

              {/* Instructions */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-medium text-gray-900 mb-3">📱 How to record on your phone:</h4>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">1.</span>
                    <span>Scan the QR code with your phone camera</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">2.</span>
                    <span>Allow microphone access when prompted</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">3.</span>
                    <span>Read the text shown on your phone</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">4.</span>
                    <span>Upload each sample (5 total)</span>
                  </li>
                </ol>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progress
                  </span>
                  <span className="text-sm text-gray-600">
                    Sample {voiceStatus?.samplesUploaded || 0} of {voiceStatus?.requiredSamples || 5}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${((voiceStatus?.samplesUploaded || 0) / (voiceStatus?.requiredSamples || 5)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Status Message */}
              {voiceStatus && voiceStatus.samplesUploaded > 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-medium">
                    ✓ Sample {voiceStatus.samplesUploaded} uploaded successfully!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {voiceStatus.requiredSamples - voiceStatus.samplesUploaded} more to go
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    ⏳ Waiting for phone... Keep this window open
                  </p>
                </div>
              )}

              {/* Switch back to desktop */}
              <button
                onClick={switchToDesktopMode}
                className="flex items-center justify-center gap-2 mx-auto px-4 py-2 text-gray-600 hover:text-gray-800 transition"
              >
                <Monitor className="w-4 h-4" />
                <span className="text-sm">Switch back to desktop recording</span>
              </button>
            </div>
          ) : (
            /* STATE 3: Recording interface */
            <div>
              {/* Instructions - Modern Design */}
              <div 
                className="mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(168, 85, 247, 0.06) 100%)',
                  borderRadius: '16px',
                  padding: '16px 20px',
                  border: '1px solid rgba(139, 92, 246, 0.15)'
                }}
              >
                <div className="flex items-start gap-3">
                  <span style={{ fontSize: '20px', marginTop: '2px' }}>💡</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Quick Tips for Best Quality</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Find a <span className="font-semibold text-purple-700">very quiet room</span> • Speak naturally with expression • Read all text completely
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Sample {(voiceStatus?.samplesUploaded || 0) + 1} of {voiceStatus?.requiredSamples || 5}
                  </span>
                  <span className="text-sm text-gray-600">
                    {Math.round(((voiceStatus?.samplesUploaded || 0) / (voiceStatus?.requiredSamples || 5)) * 100)}% Complete
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${((voiceStatus?.samplesUploaded || 0) / (voiceStatus?.requiredSamples || 5)) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Training Text Display - Interactive Recording Studio */}
              <div className="mb-8" ref={textContainerRef}>
                {/* Header with Sample Badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex items-center justify-center"
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '14px',
                        background: isRecording 
                          ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                          : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                        boxShadow: isRecording
                          ? '0 8px 24px rgba(239, 68, 68, 0.4), 0 0 20px rgba(239, 68, 68, 0.3)'
                          : '0 8px 24px rgba(124, 58, 237, 0.25), 0 2px 8px rgba(124, 58, 237, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <span className="text-white font-bold text-xl">{(voiceStatus?.samplesUploaded || 0) + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-xl tracking-tight">
                        {isRecording ? '🎤 Recording...' : 'Read This Text'}
                      </h4>
                      <p className="text-sm text-gray-500 font-medium">
                        {isRecording ? 'Follow the highlighted words' : 'Speak naturally • ~60-75 seconds'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Sample</div>
                    <div className="text-2xl font-bold text-purple-600">{(voiceStatus?.samplesUploaded || 0) + 1}/5</div>
                  </div>
                </div>

                {/* Volume Meter - Only visible when recording */}
                {isRecording && (
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Mic className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold text-gray-700">Volume</span>
                      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden relative">
                        <div 
                          className="h-full transition-all duration-100"
                          style={{
                            width: `${audioLevel}%`,
                            background: audioLevel > 70 
                              ? 'linear-gradient(90deg, #10B981 0%, #059669 100%)'
                              : audioLevel > 40
                              ? 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)'
                              : 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)',
                            boxShadow: audioLevel > 50 ? '0 0 10px rgba(139, 92, 246, 0.5)' : 'none'
                          }}
                        />
                      </div>
                      <span className="text-xs font-mono text-gray-500 w-10">{Math.round(audioLevel)}%</span>
                    </div>
                  </div>
                )}

                {/* Training Text Card with Word-by-Word Highlighting */}
                <div 
                  style={{
                    background: isRecording
                      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)'
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.04) 0%, rgba(168, 85, 247, 0.04) 100%)',
                    borderRadius: '20px',
                    padding: '32px',
                    border: isRecording ? '2px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.2)',
                    boxShadow: isRecording 
                      ? '0 4px 24px rgba(139, 92, 246, 0.15), 0 0 40px rgba(139, 92, 246, 0.1)'
                      : '0 2px 16px rgba(139, 92, 246, 0.08)',
                    position: 'relative',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Decorative Quote Icon */}
                  <div 
                    style={{
                      position: 'absolute',
                      top: '16px',
                      left: '16px',
                      fontSize: '48px',
                      opacity: isRecording ? 0.15 : 0.1,
                      fontFamily: 'Georgia, serif',
                      color: '#8B5CF6',
                      lineHeight: 1,
                      transition: 'opacity 0.3s ease'
                    }}
                  >
                    "
                  </div>
                  
                  {/* Countdown Overlay */}
                  {isCountingDown && countdown !== null && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(139, 92, 246, 0.95)',
                        borderRadius: '20px',
                        zIndex: 50
                      }}
                    >
                      <div 
                        className="text-white font-bold animate-bounce"
                        style={{
                          fontSize: '120px',
                          textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                          animation: 'pulse 1s ease-in-out'
                        }}
                      >
                        {countdown}
                      </div>
                    </div>
                  )}

                  {/* Text Content with 3D Sentence Highlighting */}
                  <div 
                    className="relative z-10"
                    style={{ 
                      fontSize: '1.0625rem',
                      lineHeight: '2.2',
                      fontWeight: '500',
                      letterSpacing: '0.015em',
                      textAlign: 'left',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      textRendering: 'optimizeLegibility',
                      WebkitFontSmoothing: 'antialiased'
                    }}
                  >
                    {currentTrainingSentences.map((sentence, index) => {
                      const isCurrent = isRecording && index === currentSentenceIndex;
                      const isPast = isRecording && index < currentSentenceIndex;
                      const isFuture = isRecording && index > currentSentenceIndex;
                      
                      return (
                        <span
                          key={index}
                          style={{
                            display: 'block',
                            marginBottom: '16px',
                            padding: isCurrent ? '12px 16px' : '8px 12px',
                            background: isCurrent 
                              ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
                              : isPast
                              ? 'rgba(156, 163, 175, 0.1)'
                              : 'transparent',
                            color: isCurrent 
                              ? '#ffffff'
                              : isPast
                              ? '#9CA3AF'
                              : '#1F2937',
                            borderRadius: '12px',
                            transform: isCurrent ? 'scale(1.02) translateX(4px)' : 'scale(1)',
                            boxShadow: isCurrent 
                              ? '0 8px 24px rgba(139, 92, 246, 0.4), 0 2px 12px rgba(139, 92, 246, 0.2)'
                              : 'none',
                            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            fontWeight: isCurrent ? '600' : '500',
                            textShadow: isCurrent ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            opacity: isFuture ? 0.5 : 1,
                            cursor: 'default',
                            borderLeft: isCurrent ? '4px solid rgba(255, 255, 255, 0.5)' : 'none'
                          }}
                        >
                          {sentence}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Helpful Tip */}
                {!isRecording && (
                  <div 
                    className="mt-4 flex items-center gap-2 justify-center"
                    style={{
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(168, 85, 247, 0.06) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(139, 92, 246, 0.12)'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>💡</span>
                    <p className="text-sm text-gray-600 font-medium">
                      Read at a natural pace with emotion matching the story
                    </p>
                  </div>
                )}

                {/* Countdown / Recording Indicator */}
                {isCountingDown && (
                  <div 
                    className="mt-4 flex items-center gap-3 justify-center"
                    style={{
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
                      borderRadius: '12px',
                      border: '2px solid rgba(139, 92, 246, 0.2)',
                      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.15)'
                    }}
                  >
                    <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                    <p className="text-sm text-purple-600 font-bold">
                      Get Ready to Read! Starting in {countdown}...
                    </p>
                  </div>
                )}
                
                {isRecording && !isCountingDown && (
                  <div 
                    className="mt-4 flex items-center gap-3 justify-center"
                    style={{
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)',
                      borderRadius: '12px',
                      border: '2px solid rgba(239, 68, 68, 0.2)',
                      boxShadow: '0 4px 16px rgba(239, 68, 68, 0.15)'
                    }}
                  >
                    <div 
                      className="w-3 h-3 bg-red-500 rounded-full animate-pulse"
                      style={{
                        boxShadow: '0 0 10px rgba(239, 68, 68, 0.6)'
                      }}
                    />
                    <p className="text-sm text-red-600 font-bold">
                      RECORDING • Follow the highlighted sentences as you read
                    </p>
                  </div>
                )}
              </div>

              {/* Recording controls */}
              <div className="flex flex-col items-center">
                {/* Timer - Show when recording */}
                {isRecording && (
                  <div className="flex flex-col items-center gap-4 mb-6">
                    <div className="text-5xl font-mono font-bold text-red-600">
                      {formatTime(recordingTime)}
                    </div>
                    <button
                      onClick={stopRecording}
                      className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl flex items-center gap-3"
                      style={{
                        boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)'
                      }}
                    >
                      <Square className="w-6 h-6" />
                      Stop Recording
                    </button>
                  </div>
                )}
                
                {/* Show microphone button only when NOT recording */}
                {!isRecording && !audioBlob && (
                  <>
                    {/* Permission warning */}
                    {micPermissionState === 'denied' && (
                  <div className="w-full max-w-md mb-4 bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 font-semibold mb-2">🎤 Microphone Access Needed</p>
                    <p className="text-sm text-amber-700 mb-2">
                      To record your voice, we need microphone permission.
                    </p>
                    <ol className="text-sm text-amber-700 space-y-1 ml-4 list-decimal mb-3">
                      <li>Click the 🔒 or ⓘ icon in your browser address bar</li>
                      <li>Find &quot;Microphone&quot; in the permissions list</li>
                      <li>Change it to &quot;Allow&quot;</li>
                      <li>Refresh this page or click check button below</li>
                    </ol>
                    <button
                      onClick={checkMicrophonePermission}
                      className="w-full bg-amber-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-amber-700 transition-all"
                    >
                      🔄 Check Permission Again
                    </button>
                  </div>
                    )}

                    {/* Microphone Record button */}
                    <button
                      onClick={startRecording}
                      disabled={uploading}
                      className="w-24 h-24 rounded-full flex items-center justify-center transition-all duration-200 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                    >
                      <Mic className="w-10 h-10 text-white" />
                    </button>

                    <p className="text-gray-600 text-center">
                      {micPermissionState === 'checking'
                        ? 'Checking microphone access...'
                        : 'Click the microphone to start recording'}
                    </p>
                  </>
                )}

                {/* Upload button */}
                {audioBlob && !uploading && (
                  <div className="w-full max-w-md">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800 text-center">
                        ✓ Recording complete! Ready to upload.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setAudioBlob(null)}
                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      >
                        Record Again
                      </button>
                      <button
                        onClick={uploadRecording}
                        className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center justify-center gap-2"
                      >
                        <Upload className="w-5 h-5" />
                        Upload Step {(voiceStatus?.samplesUploaded || 0) + 1}
                      </button>
                    </div>
                  </div>
                )}

                {uploading && (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
                    <p className="text-gray-600 font-semibold">Uploading Step {(voiceStatus?.samplesUploaded || 0) + 1}...</p>
                    <p className="text-sm text-gray-500 mt-1">Processing your voice sample</p>
                  </div>
                )}
              </div>

              {/* Error display */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Use Phone Instead button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-center text-sm text-gray-600 mb-3">
                  Having trouble with your microphone?
                </p>
                <button
                  onClick={switchToQRMode}
                  disabled={qrLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {qrLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Generating QR Code...</span>
                    </>
                  ) : (
                    <>
                      <Smartphone className="w-5 h-5" />
                      <span>Use Phone Instead</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

